import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LiveArena, ArenaParticipant } from "@/hooks/useLiveArenas";
import { useArenaInteractions } from "@/hooks/useArenaInteractions";
import { useScreenShare } from "@/hooks/useScreenShare";
import { useArenaSpotlight } from "@/hooks/useArenaSpotlight";
import { useHandRaise } from "@/hooks/useHandRaise";
import { useArenaModerationTools } from "@/hooks/useArenaModerationTools";
import { useArenaCohosts } from "@/hooks/useArenaCohosts";
import ArenaParticipantsGrid from "./ArenaParticipantsGrid";
import { SocialProofIndicator } from "./SocialProofIndicator";
import { ParticipantInteractionOverlay } from "./ParticipantInteractionOverlay";
import { EndArenaButton } from "./ArenaHostControls";
import { LiveBadge } from "./LiveBadge";
import { ArenaChatPanel } from "./ArenaChatPanel";
import { ArenaFloatingReactions } from "./ArenaFloatingReactions";
import { ScreenShareDisplay } from "./ScreenShareDisplay";
import { SpotlightView } from "./SpotlightView";
import { HandRaisePanel, HandRaiseButton } from "./HandRaisePanel";
import { ArenaModerationPanel, ArenaRulesDisplay, PinnedMessagesDisplay } from "./ArenaModerationPanel";
import { CohostManagementPanel } from "./CohostManagementPanel";
import { CohostInviteNotification } from "./CohostInviteNotification";
import { 
  Mic, MicOff, Video, VideoOff, PhoneOff, Users, MessageCircle, 
  Eye, EyeOff, Shield, Monitor, MonitorOff, Crown
} from "lucide-react";
import { cn } from "@/lib/utils";

interface ArenaRoomModalProps {
  arena: LiveArena | null;
  participants: ArenaParticipant[];
  currentUserId?: string;
  isOpen: boolean;
  onClose: () => void;
  onLeave: () => void;
  onToggleMute: () => void;
  onToggleVideo: () => void;
}

const ArenaRoomModal: React.FC<ArenaRoomModalProps> = ({
  arena,
  participants,
  currentUserId,
  isOpen,
  onClose,
  onLeave,
  onToggleMute,
  onToggleVideo,
}) => {
  const [selectedParticipant, setSelectedParticipant] = useState<ArenaParticipant | null>(null);
  const [isWatchOnly, setIsWatchOnly] = useState(false);
  const [showPortalAnimation, setShowPortalAnimation] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const { socialProof, popUser, likeUser, viewProfile, checkForMatch } = useArenaInteractions(arena?.id || null);
  const { isSharing, stream: screenStream, toggleScreenShare, stopScreenShare } = useScreenShare();
  const { spotlightedParticipant, toggleSpotlight, setSpotlight } = useArenaSpotlight(arena?.id || null, participants);
  const { raisedHands, isHandRaised, toggleHandRaise, promoteToSpeaker, dismissHandRaise } = useHandRaise(arena?.id || null, participants);
  const { 
    rules, 
    pinnedMessages, 
    isMuteAllActive, 
    toggleMuteAll, 
    updateRules, 
    addCustomRule, 
    removeCustomRule, 
    unpinMessage 
  } = useArenaModerationTools(arena?.id || null);
  const { cohosts, isCohost } = useArenaCohosts(arena?.id || null);

  // Portal animation on join
  useEffect(() => {
    if (isOpen) {
      setShowPortalAnimation(true);
      const timer = setTimeout(() => setShowPortalAnimation(false), 800);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!arena) return null;

  const currentParticipant = participants.find(p => p.user_id === currentUserId);
  const isMuted = currentParticipant?.is_muted ?? true;
  const isVideoOn = currentParticipant?.is_video_on ?? false;
  const isHost = arena.created_by === currentUserId;
  const hasModPrivileges = isHost || isCohost; // Co-hosts have mod privileges

  const handleParticipantClick = (participant: ArenaParticipant) => {
    if (participant.user_id !== currentUserId) {
      setSelectedParticipant(participant);
    }
  };

  const handlePop = async () => {
    if (selectedParticipant) {
      await popUser(selectedParticipant.user_id);
      const isMatch = await checkForMatch(selectedParticipant.user_id);
      if (isMatch) {
        // Handle match - could show match modal
        console.log("It's a match!");
      }
    }
  };

  const handleLike = async () => {
    if (selectedParticipant) {
      await likeUser(selectedParticipant.user_id);
    }
  };

  const handleViewProfile = () => {
    if (selectedParticipant) {
      viewProfile(selectedParticipant.user_id);
      // Could navigate to profile or show profile modal
    }
    setSelectedParticipant(null);
  };

  const toggleWatchOnly = () => {
    setIsWatchOnly(!isWatchOnly);
    if (!isWatchOnly && !isMuted) {
      onToggleMute();
    }
    if (!isWatchOnly && isVideoOn) {
      onToggleVideo();
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={cn(
          "max-w-md max-h-[90vh] overflow-hidden flex flex-col bg-gradient-to-br from-background to-muted/50 relative",
          showPortalAnimation && "animate-in zoom-in-95 duration-300"
        )}>
          {/* Portal animation overlay */}
          {showPortalAnimation && (
            <div className="absolute inset-0 bg-gradient-to-br from-pop/20 to-primary/20 animate-pulse z-50 pointer-events-none rounded-lg" />
          )}

          {/* Chat Panel */}
          {arena && (
            <ArenaChatPanel
              arenaId={arena.id}
              currentUserId={currentUserId}
              isOpen={isChatOpen}
              onClose={() => setIsChatOpen(false)}
            />
          )}

          {/* Co-host invite notification */}
          {arena && currentUserId && (
            <CohostInviteNotification arenaId={arena.id} userId={currentUserId} />
          )}

          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <span className="text-xl">{arena.name}</span>
                <LiveBadge variant="glow" className="scale-75" />
              </DialogTitle>
              <div className="flex items-center gap-2">
                {isHost && (
                  <>
                    <Crown className="w-4 h-4 text-amber-500" />
                    <span className="text-xs text-amber-500 font-medium">Host</span>
                  </>
                )}
                {isCohost && !isHost && (
                  <>
                    <Shield className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-blue-500 font-medium">Co-host</span>
                  </>
                )}
                {/* Host can invite co-hosts */}
                {isHost && arena && (
                  <CohostManagementPanel
                    arenaId={arena.id}
                    isHost={isHost}
                    participants={participants}
                    hostId={arena.created_by || ''}
                  />
                )}
                {/* Moderation panel for host/co-hosts */}
                {hasModPrivileges && (
                  <ArenaModerationPanel
                    isHost={hasModPrivileges}
                    rules={rules}
                    pinnedMessages={pinnedMessages}
                    isMuteAllActive={isMuteAllActive}
                    onToggleMuteAll={toggleMuteAll}
                    onUpdateRules={updateRules}
                    onAddCustomRule={addCustomRule}
                    onRemoveCustomRule={removeCustomRule}
                    onUnpinMessage={unpinMessage}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Users className="w-4 h-4" />
                <span>{participants.length} / {arena.max_participants}</span>
                {cohosts.length > 0 && (
                  <span className="text-xs text-blue-500">• {cohosts.length} co-host{cohosts.length > 1 ? 's' : ''}</span>
                )}
              </div>
              <SocialProofIndicator {...socialProof} compact />
            </div>
          </DialogHeader>

          {/* Pinned messages */}
          <PinnedMessagesDisplay messages={pinnedMessages} className="mx-4" />

          {/* Arena rules for non-moderators */}
          {!hasModPrivileges && <ArenaRulesDisplay rules={rules} className="mx-4" />}

          {/* Hand raise panel - visible to moderators */}
          {raisedHands.length > 0 && (
            <HandRaisePanel
              raisedHands={raisedHands}
              isHost={hasModPrivileges}
              onPromote={promoteToSpeaker}
              onDismiss={dismissHandRaise}
              className="mx-4"
            />
          )}

          {/* Social proof banner when heating up */}
          {socialProof.isHeatingUp && (
            <div className="mx-4 py-2 px-4 bg-gradient-to-r from-orange-500/20 to-red-500/20 rounded-full text-center text-sm font-medium text-orange-600 animate-pulse">
              🔥 Arena is heating up! {socialProof.recentPops} Pops in last 2 mins
            </div>
          )}

          {/* Screen share display */}
          {isSharing && screenStream && (
            <div className="px-4 mb-4">
              <ScreenShareDisplay
                stream={screenStream}
                isLocal
                onClose={stopScreenShare}
                className="h-40"
              />
            </div>
          )}

          {/* Spotlight view */}
          {spotlightedParticipant && (
            <div className="px-4 mb-4">
              <SpotlightView
                participant={spotlightedParticipant}
                isHost={isHost}
                onRemoveSpotlight={() => setSpotlight(null)}
              />
            </div>
          )}

          {/* Participants grid */}
          <div className="flex-1 overflow-y-auto py-4 relative">
            <ArenaParticipantsGrid
              participants={participants}
              currentUserId={currentUserId}
              onParticipantClick={handleParticipantClick}
              isHost={isHost}
              hasModPrivileges={hasModPrivileges}
              spotlightedUserId={spotlightedParticipant?.user_id}
              onSpotlightUser={toggleSpotlight}
              cohostUserIds={cohosts.map(c => c.user_id)}
            />
            
            {/* Floating reactions */}
            {arena && (
              <ArenaFloatingReactions arenaId={arena.id} />
            )}
          </div>

          {/* Controls */}
          <div className="pt-4 border-t border-border">
            {/* Watch-only toggle */}
            <div className="flex items-center justify-center mb-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleWatchOnly}
                className={cn(
                  "gap-2 rounded-full",
                  isWatchOnly && "bg-muted"
                )}
              >
                {isWatchOnly ? (
                  <>
                    <Eye className="w-4 h-4" />
                    <span>Watch-only Mode</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4" />
                    <span>Participating</span>
                  </>
                )}
              </Button>
            </div>

            <div className="flex items-center justify-center gap-4">
              {/* Mute toggle */}
              <Button
                variant="outline"
                size="lg"
                onClick={onToggleMute}
                disabled={isWatchOnly}
                className={cn(
                  "rounded-full w-14 h-14 transition-all",
                  isMuted ? "bg-red-500/10 border-red-500 text-red-500" : "bg-green-500/10 border-green-500 text-green-500",
                  isWatchOnly && "opacity-50"
                )}
              >
                {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
              </Button>

              {/* Video toggle */}
              {arena.is_video_enabled && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onToggleVideo}
                  disabled={isWatchOnly}
                  className={cn(
                    "rounded-full w-14 h-14 transition-all",
                    isVideoOn ? "bg-green-500/10 border-green-500 text-green-500" : "bg-muted border-border text-muted-foreground",
                    isWatchOnly && "opacity-50"
                  )}
                >
                  {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                </Button>
              )}

              {/* Screen share toggle (host only) */}
              {isHost && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={toggleScreenShare}
                  className={cn(
                    "rounded-full w-14 h-14 transition-all",
                    isSharing ? "bg-blue-500/10 border-blue-500 text-blue-500" : "bg-muted border-border text-muted-foreground"
                  )}
                >
                  {isSharing ? <Monitor className="w-6 h-6" /> : <MonitorOff className="w-6 h-6" />}
                </Button>
              )}

              {/* Hand raise (listeners only) */}
              {!currentParticipant?.is_speaker && (
                <HandRaiseButton
                  isHandRaised={isHandRaised}
                  onToggle={toggleHandRaise}
                  disabled={isWatchOnly}
                />
              )}

              {/* Chat */}
              <Button
                variant="outline"
                size="lg"
                onClick={() => setIsChatOpen(true)}
                className={cn(
                  "rounded-full w-14 h-14 relative",
                  isChatOpen && "bg-primary/10 border-primary text-primary"
                )}
              >
                <MessageCircle className="w-6 h-6" />
              </Button>

              {/* Leave button */}
              <Button
                variant="destructive"
                size="lg"
                onClick={onLeave}
                className="rounded-full w-14 h-14"
              >
                <PhoneOff className="w-6 h-6" />
              </Button>
            </div>

            {/* Host controls */}
            {isHost && (
              <div className="flex justify-center mt-4">
                <EndArenaButton onEndArena={onLeave} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Participant interaction overlay */}
      <ParticipantInteractionOverlay
        participant={selectedParticipant!}
        isOpen={!!selectedParticipant}
        onClose={() => setSelectedParticipant(null)}
        onPop={handlePop}
        onLike={handleLike}
        onViewProfile={handleViewProfile}
      />
    </>
  );
};

export default ArenaRoomModal;
